import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { Save } from "lucide-react";

const PROFESSIONS = [
  ["medecin", "Médecin"], ["psychologue", "Psychologue"], ["infirmier", "Infirmier(ère)"],
  ["dentiste", "Chirurgien-dentiste"], ["sage-femme", "Sage-femme"],
  ["kinesitherapeute", "Kinésithérapeute"], ["autre", "Autre"],
];
const EMPTY = {
  civilite: "", prenom: "", nom: "", email_principal: "", telephone: "",
  statut_pro: "", profession_libre: "", type: "VISITEUR", statut: "PROSPECT",
  institut_id: "", date_adhesion: "", date_fin_adhesion: "", date_naissance: "",
  consentement_annuaire: false, consentement_newsletter: false,
  tags: [],
};

function toInputDate(d) {
  if (!d) return "";
  return new Date(d).toISOString().slice(0, 10);
}

export default function MemberForm({ edit = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [instituts, setInstituts] = useState([]);
  const [professions, setProfessions] = useState(PROFESSIONS.map(([code, libelle]) => ({ code, libelle })));
  const [tags, setTags] = useState([]);
  const [types, setTypes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/admin/instituts").then(setInstituts).catch(() => {});
    api("/admin/professions").then(setProfessions).catch(() => {});
    api("/admin/tags").then(setTags).catch(() => {});
    api("/referentiels/labels").then((l) => setTypes(l.types)).catch(() => {});
    if (edit && id) {
      api(`/admin/membres/${id}`).then((d) => {
        const p = d.person;
        setForm({
          civilite: p.civilite || "", prenom: p.prenom || "", nom: p.nom || "",
          email_principal: p.email_principal || "", telephone: p.telephone || "",
          statut_pro: p.statut_pro || "", profession_libre: p.profession_libre || "",
          type: p.type || "VISITEUR", statut: p.statut || "PROSPECT",
          institut_id: p.institut_id || "",
          date_adhesion: toInputDate(p.date_adhesion),
          date_fin_adhesion: toInputDate(p.date_fin_adhesion),
          date_naissance: toInputDate(p.date_naissance),
          consentement_annuaire: !!p.consentement_annuaire,
          consentement_newsletter: !!p.consentement_newsletter,
          tags: p.tags || [],
        });
      }).catch((e) => toast.error(e.message));
    }
  }, [edit, id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      ...form,
      institut_id: form.institut_id || null,
      date_adhesion: form.date_adhesion || null,
      date_fin_adhesion: form.date_fin_adhesion || null,
      date_naissance: form.date_naissance || null,
      emails_secondaires: [],
      tags: form.tags,
    };
    try {
      const res = edit
        ? await api(`/admin/membres/${id}`, { method: "PUT", body })
        : await api("/admin/membres", { method: "POST", body });
      toast.success(edit ? "Membre mis à jour" : "Membre créé");
      navigate(`/admin/membres/${res.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary";
  const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";

  return (
    <AppLayout variant="admin">
      <div data-testid="member-form-page" className="max-w-3xl space-y-6 animate-fade-in">
        <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">
          {edit ? "Modifier le membre" : "Nouveau membre"}
        </h1>

        <form onSubmit={submit} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Civilité</label>
              <select data-testid="form-civilite-select" className={inputCls} value={form.civilite} onChange={(e) => set("civilite", e.target.value)}>
                <option value="">—</option><option value="Mme">Mme</option><option value="M.">M.</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Prénom *</label>
              <input data-testid="form-prenom-input" required className={inputCls} value={form.prenom} onChange={(e) => set("prenom", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Nom *</label>
              <input data-testid="form-nom-input" required className={inputCls} value={form.nom} onChange={(e) => set("nom", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email principal *</label>
              <input data-testid="form-email-input" type="email" required className={inputCls} value={form.email_principal} onChange={(e) => set("email_principal", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input data-testid="form-telephone-input" className={inputCls} value={form.telephone} onChange={(e) => set("telephone", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Type</label>
              <select data-testid="form-type-select" className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)}>
                {types
                  ? Object.values(types).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)
                  : (<><option value="VISITEUR">Visiteur</option><option value="PRATICIEN">Praticien</option></>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Statut</label>
              <select data-testid="form-statut-select" className={inputCls} value={form.statut} onChange={(e) => set("statut", e.target.value)}>
                <option value="PROSPECT">Prospect</option>
                <option value="ACTIF">Actif</option>
                <option value="EXPIRE">Expiré</option>
                <option value="SUSPENDU">Suspendu</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Profession</label>
              <select data-testid="form-profession-select" className={inputCls} value={form.statut_pro} onChange={(e) => set("statut_pro", e.target.value)}>
                <option value="">—</option>
                {professions.map((p) => <option key={p.code} value={p.code}>{p.libelle}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Institut de rattachement</label>
              <select data-testid="form-institut-select" className={inputCls} value={form.institut_id} onChange={(e) => set("institut_id", e.target.value)}>
                <option value="">Aucun</option>
                {instituts.map((i) => <option key={i.id} value={i.id}>{i.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Date d'anniversaire</label>
              <input data-testid="form-date-naissance-input" type="date" className={inputCls} value={form.date_naissance} onChange={(e) => set("date_naissance", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Date d'adhésion</label>
              <input data-testid="form-date-adhesion-input" type="date" className={inputCls} value={form.date_adhesion} onChange={(e) => set("date_adhesion", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Fin d'adhésion</label>
              <input data-testid="form-date-fin-input" type="date" className={inputCls} value={form.date_fin_adhesion} onChange={(e) => set("date_fin_adhesion", e.target.value)} />
            </div>
          </div>
          <fieldset className="rounded-md border border-slate-200 p-4">
            <legend className="text-sm font-medium text-cfhtb-heading px-1">Tags</legend>
            <div className="flex flex-wrap gap-2" data-testid="member-tags-selector">
              {tags.length === 0 && (
                <p className="text-xs text-cfhtb-text/70">Aucun tag — créez-en dans Administration → Tags.</p>
              )}
              {tags.map((t) => {
                const active = form.tags.includes(t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    data-testid={`member-tag-${t.slug}`}
                    onClick={() => set("tags", active ? form.tags.filter((x) => x !== t.id) : [...form.tags, t.id])}
                    className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${active ? "text-white" : "bg-white"}`}
                    style={active
                      ? { backgroundColor: t.couleur, borderColor: t.couleur }
                      : { borderColor: t.couleur, color: t.couleur }}
                  >
                    {t.nom}
                  </button>
                );
              })}
            </div>
          </fieldset>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-cfhtb-text cursor-pointer">
              <input type="checkbox" data-testid="form-consent-annuaire" checked={form.consentement_annuaire}
                onChange={(e) => set("consentement_annuaire", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
              Consentement annuaire
            </label>
            <label className="flex items-center gap-2 text-sm text-cfhtb-text cursor-pointer">
              <input type="checkbox" data-testid="form-consent-newsletter" checked={form.consentement_newsletter}
                onChange={(e) => set("consentement_newsletter", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
              Consentement newsletter
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" data-testid="form-cancel-button" onClick={() => navigate(-1)}
              className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors">
              Annuler
            </button>
            <button type="submit" data-testid="form-save-button" disabled={busy}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60">
              <Save className="h-4 w-4" /> {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
